import { setAttribute } from '../../dom';
import { platform } from '../../platform';

/**
 * Verifica se o elemento pode ser focado sem precisar de tabindex.
 *
 * @param el Elemento a ser verificado.
 *
 * @category Focus
 */
export const checkIsFocusable = (el: Element) =>
  el.tagName === 'BUTTON' ||
  el.tagName === 'INPUT' ||
  el.tagName === 'TEXTAREA' ||
  el.tagName === 'A' ||
  el.tagName === 'SELECT' ||
  el.tagName === 'SUMMARY' ||
  el.hasAttribute('tabindex');

const setTabindexIfNecessary = (el: HTMLElement) => {
  if (checkIsFocusable(el)) {
    return;
  }

  setAttribute(el, 'tabindex', '-1');
};

/**
 * O VoiceOver tem uma manipulação de foco automática dele que serve muito bem em vários
 * casos. Como queremos personalizar o foco e não é sabido quando ele vai executar,
 * o foco personalizado é feito 10 vezes, para garantir que se o foco automático
 * acontecer nesse meio tempo, ele será sobreposto pelo foco personalizado.
 *
 * @param el Elemento para receber foco.
 *
 * @category Focus
 */
export const setVoiceOverFocus = (
  el: HTMLElement,
  { focusInterval = 10, focusTotalRepetitions = 10, noBlur = false } = {},
) =>
  new Promise<void>((resolve) => {
    if (!noBlur) {
      el.blur();
    }

    let focusRepetitions = 0;
    const interval = setInterval(() => {
      el.focus();
      focusRepetitions++;

      if (focusRepetitions >= focusTotalRepetitions) {
        clearInterval(interval);
        resolve();
      }
    }, focusInterval);
  });

/**
 * Torna elementos focaveis, em seguida foca no elemento e remove a possibilidade de foco dele.
 *
 * @param element elemento para receber foco
 *
 * @category Focus
 */
export const focus = (element: HTMLElement) => {
  setTabindexIfNecessary(element);

  // Com VoiceOver, se o foco é movido imediatamente a leitura do novo elemento
  // em foco não é feita corretamente. Essa técnica cria um outro
  // problema menor que repete a leitura do elemento que foi feito blur e é interronmpida
  // pela nova leitura.
  // Em alguns NVDA o foco virtual não segue o nativo, então essa técnica é
  // aplicada para o Windows também.
  if (platform.os === 'IOS') {
    setVoiceOverFocus(element);

    return;
  }

  if (platform.os === 'Windows') {
    setTimeout(() => element.focus());

    return;
  }

  element.focus();
};

const checkIsElementToFocus = (el: Element) => {
  const isText = typeof el.firstChild === 'string' || el.firstChild instanceof Text;
  const isControl = checkIsFocusable(el);
  const isNotAriaHidden = el.getAttribute('aria-hidden') !== 'true';

  return (isText || isControl) && isNotAriaHidden;
};

const findFirstElementToFocus = (el: Element): Element => {
  const children = Array.from(el.children);

  if (!children.length) {
    return el;
  }

  const foundEl = children.reduce((found, child) => {
    if (checkIsElementToFocus(found)) {
      return found;
    }

    if (checkIsElementToFocus(child)) {
      return child;
    }

    if (child.getAttribute('aria-hidden') === 'true') {
      return found;
    }

    if (child.firstChild) {
      return findFirstElementToFocus(child);
    }

    return found;
  }, el);

  return foundEl as HTMLElement;
};

/**
 * Inicia o foco em algum elemento que entrou em tela. Dependendo da plataforma
 * e do tipo de elemento, irá buscar o primeiro elemento com texto para iniciar o foco.
 *
 * @param el Elemento base para iniciar o foco.
 *
 * @category Focus
 */
export const startFocus = (el: HTMLElement) => {
  const isDialog = el.getAttribute('role') === 'dialog' || el.tagName === 'DIALOG';

  if (platform.os === 'Android' && isDialog) {
    focus(el);

    return;
  }

  focus(findFirstElementToFocus(el) as HTMLElement);
};

/**
 * Verifica se o elemento está em foco.
 *
 * @param el Elemento a ser verificado.
 *
 * @category Focus
 */
export const hasFocus = (el: HTMLElement): boolean => document.activeElement === el;

/**
 * Em caso de projetos MFE, o elemento ativo pode estar dentro de um ShadowRoot.
 * Nesse caso o document.activeElement vai sempre ser o projeto pai da aplicaçao (mfe-shell).
 * Verifica se existe o shadowRoot e retorna o elemento ativo dentro dele
 */
export const getActiveElement = (root: Document | ShadowRoot = document): Element | null => {
  const activeEl = root.activeElement;

  if (activeEl?.shadowRoot) {
    return activeEl.shadowRoot.activeElement;
  }

  return activeEl;
};
